import { Component, Inject, OnInit, ViewChild } from "@angular/core";
import { Router, ActivatedRoute, NavigationStart, NavigationEnd } from "@angular/router";
import { Observable, Subject, Subscription } from "rxjs";
import { filter, takeUntil } from "rxjs/operators";
import { Title } from "@angular/platform-browser";

import {
  Config,
  ConfigService,
  BrokerService,
  AuthService,
  HttpService,
  ViewData,
  Link,
} from "./common";
import { Person } from "./common/resources";
import { environment } from "src/environments/environment";
import {
  MsalService,
  MsalBroadcastService,
  MSAL_GUARD_CONFIG,
  MsalGuardConfiguration,
} from "@azure/msal-angular";
import {
  AccountInfo,
  EventMessage,
  EventType,
  InteractionType,
  PopupRequest,
  RedirectRequest,
} from "@azure/msal-browser";
import { MatSidenav } from "@angular/material/sidenav";

export interface HeaderModel {
  siteTitle: string;
  pageTitle?: string;
  link?: Link;
  user?: Person;
  testingMode?: boolean;
  isRefreshing: Observable<boolean>;
}

@Component({
  selector: "app",
  templateUrl: "./app.html",
  styleUrls: ["./app.css"],
  standalone: false,
})
export class App implements OnInit {
  @ViewChild("sidenav") sidenav: MatSidenav;

  showSubmenu: boolean = false;
  isShowing = false;
  showSubSubMenu: boolean = false;

  headerModel: HeaderModel;
  appTitle: string;
  config: Config;
  subscriptions: Subscription[] = [];
  needsReload: boolean;
  showSidebar: boolean = false;
  currentUser: AccountInfo = null;
  isExpanded = false;
  element: HTMLElement;
  browserSupportAllFeatures = true;
  isIframe = false;
  loggedIn = false;
  private readonly _destroying$ = new Subject<void>();

  toggleActive(event: any) {
    event.preventDefault();
    if (this.element !== undefined) {
      this.element.style.backgroundColor = "white";
    }
    let target = event.currentTarget;
    target.style.backgroundColor = "gray";
    this.element = target;
  }
  constructor(
    @Inject(MSAL_GUARD_CONFIG) private msalGuardConfig: MsalGuardConfiguration,
    private msalService: MsalService,
    private msalBroadcastService: MsalBroadcastService,
    private titleService: Title,
    private configService: ConfigService,
    private broker: BrokerService,
    private authService: AuthService,
    private http: HttpService,
    private router: Router,
    private route: ActivatedRoute
  ) {
    this.headerModel = { siteTitle: "WIMS", isRefreshing: http.hasPendingRequests };
    this.appTitle = titleService.getTitle();
    this.config = new Config();
  }

  ngOnInit(): void {
    this.isIframe = window !== window.parent && !window.opener;

    this.checkAccount();

    this.msalBroadcastService.msalSubject$
      .pipe(
        filter(
          (msg: EventMessage) =>
            msg.eventType === EventType.LOGIN_SUCCESS ||
            msg.eventType === EventType.ACQUIRE_TOKEN_SUCCESS
        ),
        takeUntil(this._destroying$)
      )
      .subscribe((result) => {
        this.checkAccount();
      });

    this.authService.getCurrenUser().subscribe((user) => {
      this.headerModel.user = user;
    });

    this.router.events
      .pipe(filter((event) => event instanceof NavigationStart))
      .subscribe((navigationStartEvent) => {
        if (environment.production && this.needsReload) {
          window.location.reload();
        }
      });

    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe((event) => {
        this.broker.broadcast("navigation.ended", this.route);
      });

    this.configService.get();

    this.subscriptions.push(
      this.broker.on<Config>("config.service.current").subscribe((config) => {
        this.config = config;
        this.headerModel.testingMode = config.testingMode;
      }),

      this.broker.on<ViewData>("view.service.changed").subscribe((event: ViewData) => {
        this.headerModel.pageTitle = event.pageTitle;
        this.headerModel.link = event.link;
        this.titleService.setTitle(`${this.appTitle} - ${event.pageTitle}`);
      })

      // this.broker.on("http.service.app.version")
      //     .subscribe( appVersion => {
      //         if (appVersion) {
      //             this.needsReload = this.appVersion !== appVersion
      //         }
      //     })
    );

    this.broker.broadcast("navigation.ended", this.route);
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((subscription) => subscription.unsubscribe());
  }

  checkAccount() {
    this.loggedIn = this.msalService.instance.getAllAccounts().length > 0;
    this.currentUser = this.loggedIn ? this.msalService.instance.getAllAccounts()[0] : null;
  }

  login() {
    if (this.msalGuardConfig.interactionType === InteractionType.Popup) {
      if (this.msalGuardConfig.authRequest) {
        this.msalService
          .loginPopup({ ...this.msalGuardConfig.authRequest } as PopupRequest)
          .subscribe(() => this.checkAccount());
      } else {
        this.msalService.loginPopup().subscribe(() => this.checkAccount());
      }
    } else {
      if (this.msalGuardConfig.authRequest) {
        this.msalService.loginRedirect({ ...this.msalGuardConfig.authRequest } as RedirectRequest);
      } else {
        this.msalService.loginRedirect();
      }
    }
  }

  logout() {
    if (this.loggedIn) this.msalService.logout();
  }
}
